import dayjs from 'dayjs';
import type { User } from '~/types/user-schema';
import type { AccessType, MiddlewareMeta } from '~/types/access/access-types';
import { normalizeAccessOptions } from '~/utils/access-helper';

/**
 * Get the locale path
 */
function localeNavigate(path: string) {
    const localePath = useLocalePath();

    return navigateTo(localePath(path));
}

/**
 * Navigate the user to his dashboard
 *
 * @param {MaybeRef<User> | undefined | null} profile
 * @param {AccessType} type
 * @return {*}
 */
function navigateToDashboard(
    profile: MaybeRef<User> | undefined | null,
    type: AccessType,
) {
    if (!profile) {
        return localeNavigate('/');
    }

    if (type === 'member' || !unref(profile)?.frontendManagementAccess) {
        return localeNavigate('/members/dashboard');
    }

    return localeNavigate(getAdminDashboardFromProfile(profile));
}

/**
 * Check if one of the users roles is in the allowed roles
 *
 * @param {string[]} roles
 * @param {string[]} allowedRoles
 * @return {boolean}
 */
function hasOneOfRoles(roles: string[], allowedRoles: string[]) {
    return roles.some((role) => allowedRoles.includes(role));
}

// TODO put elsewhere
declare module '#app' {
    interface PageMeta {
        access?: MiddlewareMeta;
    }
}

declare module 'vue-router' {
    interface RouteMeta {
        access?: MiddlewareMeta;
    }
}

/**
 * Middleware to check if the user has access to the page
 * @param {Route} to
 */
export default defineNuxtRouteMiddleware(async (to) => {
    const accessOptions = normalizeAccessOptions(to.meta?.access);
    if (accessOptions.type === null) {
        return;
    }

    // Page access type
    const isAdminPage = accessOptions.type === 'admin';
    const isMemberPage = accessOptions.type === 'member';
    // is the page guest only? Allow access if the user is not authenticated
    const isGuestOnly = accessOptions.type === 'guest';

    const { user, loggedIn, session } = useUserSession();

    // Check if expiresAt date on session is in the past
    const isExpired = isSessionExpired(session);

    const authorized = !isExpired && loggedIn.value;

    if (isGuestOnly && !authorized) {
        // If the user is not authenticated and the page is guest only, allow access
        return;
    } else if (isGuestOnly && authorized) {
        // If the user is authenticated and the page is guest only, redirect to the dashboard
        return navigateToDashboard(user.value, accessOptions.type);
    } else if (authorized && isMemberPage) {
        // If the user is authenticated and the page is member only, allow access
        return;
    }

    if (!authorized) {
        // If the user is not authenticated and the page is not guest only, redirect to login
        return abortNavigation({
            statusCode: 401,
            statusMessage: 'Unauthorized',
        });
    }

    const hasManagementAccess = user.value?.frontendManagementAccess ?? false;

    if (isAdminPage && !hasManagementAccess) {
        // If the user is not a manager/admin and the page is admin only, redirect to the dashboard
        return navigateToDashboard(user.value, 'member');
    }

    const isRestrictedToRoles = accessOptions.roles.length > 0;

    if (isAdminPage && hasManagementAccess && !isRestrictedToRoles) {
        // If the user is a manager/admin, allow access to admin pages
        return;
    }

    const { urlParams } = useAdminUrl();

    // Role restrictions
    if (isRestrictedToRoles) {
        const product = urlParams.value?.product ?? user.value?.lastUsedProduct;

        if (product) {
            const productAccess = user.value?.productAccess[product] ?? {};

            const roles = productAccess?.roles ?? [];
            if (hasOneOfRoles(roles, accessOptions.roles)) {
                // If the user has the required role, allow access
                return;
            }
        }
    }

    // Fall back to login page
    return localeNavigate('/');
});
